import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Space, Statistic, Table, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
    BasicHolding,
    getBasicBook,
    getUnclaimedCash,
    syncBasicBook,
} from '../../services/basicBookApi';
import { formatInt } from '../../utils/numberFormat';
import GridImportModal from './GridImportModal';

const { Text } = Typography;

/**
 * Shares at the broker that no strategy claims.
 *
 * NOT A PORTFOLIO. Nothing was moved here, there is no account behind it and no cash
 * operation runs against it: the rows are the broker's snapshot minus what every strategy's
 * wallet says it owns. Two things follow, and both are the reason this screen looks as thin
 * as it does.
 *
 * First, the only write is Sync. The numbers are an inference, so the one thing anyone can
 * do to them is refresh the input — a broker snapshot taken this morning does not know about
 * a dividend credited at lunchtime, and a row that looks wrong is usually a stale snapshot
 * rather than a wrong subtraction.
 *
 * Second, a symbol arrives here by a wallet CLOSING — an ex-rights hand-over, most often —
 * and leaves by being imported into a grid again. That is the whole life cycle, and Import
 * is the only other button because it is the only other thing that can happen.
 *
 * UNITS: every money column is raw VND. Cost is derived — the broker reports one average for
 * the account, not one per strategy — so it is an estimate of what these particular shares
 * cost, and the import screen says so where the user is asked to confirm it.
 *
 * SHARED BY TWO PAGES, which is why `dataConfigId` is optional. On the grid page there is a
 * config for an import to land in; on the portfolio page there is not, and Import is not
 * shown at all — importing is a grid action, not a portfolio one. The reading half — what you
 * hold, what it cost, what cash is loose — is the same on both, and duplicating it would
 * have meant two answers to one question.
 */

interface Props {
  portfolioId?: string | null;
  /** The grid config an import lands in. No config, no import — the rows still list. */
  dataConfigId?: string | null;
  /** Bumped by the page when something else changed the books. */
  refreshKey?: number;
  /** False while the tab is hidden, so a background tab does not poll the broker. */
  active?: boolean;
  /** An import changed the grid's config and wallets: the page reloads what shows them. */
  onImported?: () => void;
}

/** Rows that are the broker's last answer, not a fresh read, say so and when. */
const SourceNote: React.FC<{ rows: BasicHolding[] }> = ({ rows }) => {
  const r = rows[0];
  if (!r?.stale) return null;
  const age = Math.max(0, Math.round((Date.now() - new Date(r.capturedAt).getTime()) / 60000));
  return (
    <Alert type="warning" showIcon style={{ fontSize: 12 }}
      message={`The broker could not be re-read: this is its last answer, ${age} min ago.`} />
  );
};

const BasicHoldingsTab: React.FC<Props> = ({
  portfolioId,
  dataConfigId,
  refreshKey,
  active,
  onImported,
}) => {
  const [rows, setRows] = useState<BasicHolding[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState<BasicHolding | null>(null);
  const [cash, setCash] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!portfolioId) {
      setRows([]);
      setCash(null);
      return;
    }
    setLoading(true);
    try {
      setRows(await getBasicBook(portfolioId));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
    // Separately, and never failing the table with it: the shares are the answer people
    // came for, and a broker that cannot report cash should not blank them out.
    try {
      setCash(await getUnclaimedCash(portfolioId));
    } catch {
      setCash(null);
    }
  }, [portfolioId]);

  useEffect(() => {
    if (active === false) return;
    void load();
  }, [load, refreshKey, active]);

  const sync = async () => {
    if (!portfolioId) return;
    setSyncing(true);
    try {
      const fresh = await syncBasicBook(portfolioId);
      setRows(fresh);
      message.success(
        fresh[0]?.stale ? 'The broker could not be re-read — showing its last answer.' : 'Re-read from the broker.',
      );
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Could not reach the broker.');
    } finally {
      setSyncing(false);
    }
  };

  const NUM: React.CSSProperties = { fontVariantNumeric: 'tabular-nums' };
  const columns: ColumnsType<BasicHolding> = [
    { title: 'Symbol', dataIndex: 'symbol', render: (v: string) => <Text strong>{v}</Text> },
    {
      title: 'Shares',
      dataIndex: 'quantity',
      align: 'right',
      render: (v: number) => <span style={NUM}>{formatInt(v)}</span>,
    },
    {
      title: 'Cost (VND)',
      dataIndex: 'cost',
      align: 'right',
      render: (v: number | null) => <span style={NUM}>{v == null ? '—' : formatInt(v)}</span>,
    },
    {
      title: 'Average (VND)',
      dataIndex: 'averagePrice',
      align: 'right',
      render: (v: number | null) => <span style={NUM}>{v == null ? '—' : formatInt(Math.round(v))}</span>,
    },
    // Only where there is a grid config to land in; the portfolio page lists, it does not import.
    ...(dataConfigId
      ? [
          {
            title: '',
            align: 'right' as const,
            width: 96,
            render: (_: unknown, row: BasicHolding) => (
              <Tooltip title="Put these shares back under the grid. Nothing moves at the broker.">
                <Button size="small" type="primary" ghost onClick={() => setImporting(row)}>
                  Import
                </Button>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%', padding: 12 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Shares your broker holds that no strategy claims. They are yours either way — the grid simply is not
        trading them.
      </Text>

      <SourceNote rows={rows} />

      {/* The cash half of the same view. `availableToDeposit`, not the broker balance and
          not purchasing power: gross cash minus what wallets already hold idle. Shown here
          because "my untraded portfolio" is shares AND cash, and this figure appears
          nowhere else — the balance panel shows PP, which is a different number. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Tooltip title="Cash at the broker that no strategy has been funded with. This is what a deposit could still draw on — not your purchasing power, which also counts cash already allocated but not yet spent.">
          <Statistic
            // Labelled so a test can read this figure without reaching into antd's markup:
            // "—" and "0" are opposite facts here and the assertion has to be exact.
            data-testid="unclaimed-cash"
            title={<Text type="secondary" style={{ fontSize: 12 }}>Cash not allocated to any strategy</Text>}
            value={cash == null ? '—' : formatInt(cash)}
            suffix={cash == null ? undefined : <span style={{ fontSize: 13, color: '#848E9C' }}>VND</span>}
            valueStyle={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          />
        </Tooltip>
        <Button size="small" icon={<ReloadOutlined />} loading={syncing} onClick={sync}>
          Sync with broker
        </Button>
      </div>

      <Table<BasicHolding>
        size="small"
        rowKey={(r) => `${r.exchange}:${r.symbol}`}
        loading={loading}
        pagination={false}
        dataSource={rows}
        columns={columns}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Every share your broker holds is claimed by a strategy."
            />
          ),
        }}
      />

      <GridImportModal
        open={importing != null}
        dataConfigId={dataConfigId}
        holding={importing}
        onClose={() => setImporting(null)}
        onImported={() => {
          void load();
          onImported?.();
        }}
      />
    </Space>
  );
};

export default BasicHoldingsTab;
